import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type AdminTheme = 'system' | 'light' | 'dark';

interface UiPreferences {
  theme: AdminTheme;
  notificationsEnabled: boolean;
  sidebarCollapsed: boolean;
}

@Injectable({ providedIn: 'root' })
export class UiPreferencesService {
  private readonly storageKey = 'afrotchop.admin.ui.preferences.v1';
  private readonly defaultPreferences: UiPreferences = {
    theme: 'system',
    notificationsEnabled: false,
    sidebarCollapsed: false,
  };

  private readonly themeSubject = new BehaviorSubject<AdminTheme>(this.defaultPreferences.theme);
  private readonly notificationsEnabledSubject = new BehaviorSubject<boolean>(
    this.defaultPreferences.notificationsEnabled
  );
  private readonly sidebarCollapsedSubject = new BehaviorSubject<boolean>(
    this.defaultPreferences.sidebarCollapsed
  );

  readonly theme$ = this.themeSubject.asObservable();
  readonly notificationsEnabled$ = this.notificationsEnabledSubject.asObservable();
  readonly sidebarCollapsed$ = this.sidebarCollapsedSubject.asObservable();

  private mediaQuery: MediaQueryList | null = null;

  constructor() {
    const saved = this.readPreferences();
    this.themeSubject.next(saved.theme);
    this.notificationsEnabledSubject.next(saved.notificationsEnabled);
    this.sidebarCollapsedSubject.next(saved.sidebarCollapsed);
    this.applyTheme(saved.theme);
    this.bindSystemThemeChanges();
  }

  get theme(): AdminTheme {
    return this.themeSubject.value;
  }

  get notificationsEnabled(): boolean {
    return this.notificationsEnabledSubject.value;
  }

  get sidebarCollapsed(): boolean {
    return this.sidebarCollapsedSubject.value;
  }

  setTheme(theme: AdminTheme): void {
    this.themeSubject.next(theme);
    this.applyTheme(theme);
    this.persist();
  }

  setNotificationsEnabled(enabled: boolean): void {
    this.notificationsEnabledSubject.next(enabled);
    this.persist();
  }

  setSidebarCollapsed(collapsed: boolean): void {
    this.sidebarCollapsedSubject.next(collapsed);
    this.persist();
  }

  toggleSidebar(): void {
    this.setSidebarCollapsed(!this.sidebarCollapsedSubject.value);
  }

  private persist(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const payload: UiPreferences = {
      theme: this.themeSubject.value,
      notificationsEnabled: this.notificationsEnabledSubject.value,
      sidebarCollapsed: this.sidebarCollapsedSubject.value,
    };
    window.localStorage.setItem(this.storageKey, JSON.stringify(payload));
  }

  private readPreferences(): UiPreferences {
    if (typeof window === 'undefined') {
      return this.defaultPreferences;
    }

    const raw = window.localStorage.getItem(this.storageKey);
    if (!raw) {
      return this.defaultPreferences;
    }

    try {
      const parsed = JSON.parse(raw) as Partial<UiPreferences>;
      return {
        theme: this.parseTheme(parsed.theme),
        notificationsEnabled: parsed.notificationsEnabled === true,
        sidebarCollapsed: parsed.sidebarCollapsed === true,
      };
    } catch {
      return this.defaultPreferences;
    }
  }

  private parseTheme(value: unknown): AdminTheme {
    if (value === 'light' || value === 'dark' || value === 'system') {
      return value;
    }
    return this.defaultPreferences.theme;
  }

  private applyTheme(theme: AdminTheme): void {
    if (typeof document === 'undefined') {
      return;
    }

    const effectiveTheme = theme === 'system' ? this.getSystemTheme() : theme;
    document.documentElement.setAttribute('data-theme', effectiveTheme);
    document.documentElement.style.colorScheme = effectiveTheme;
  }

  private bindSystemThemeChanges(): void {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return;
    }

    this.mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => {
      if (this.themeSubject.value === 'system') {
        this.applyTheme('system');
      }
    };

    if (typeof this.mediaQuery.addEventListener === 'function') {
      this.mediaQuery.addEventListener('change', listener);
      return;
    }

    if (typeof this.mediaQuery.addListener === 'function') {
      this.mediaQuery.addListener(listener);
    }
  }

  private getSystemTheme(): 'light' | 'dark' {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return 'light';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
}
